import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: string;
  ogImage?: string;
  structuredData?: object;
}

export default function SEOHead({ title, description, keywords, canonicalUrl, ogType = 'website', ogImage, structuredData }: SEOHeadProps) {
  const fullTitle = `${title} | 无翼鸟漫画`;
  const baseUrl = 'https://wingless-bird-manga.readdy.ai';
  const location = useLocation();

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${baseUrl}${canonicalUrl}`;

    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', `${baseUrl}${canonicalUrl}`, true);
    setMeta('og:site_name', '无翼鸟漫画', true);
    setMeta('og:locale', 'zh_CN', true);
    if (ogImage) {
      setMeta('og:image', ogImage, true);
      setMeta('og:image:width', '1200', true);
      setMeta('og:image:height', '630', true);
    }

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    if (ogImage) {
      setMeta('twitter:image', ogImage);
    }

    const existingScript = document.querySelector('script[data-jsonld="true"]');
    if (existingScript) {
      existingScript.remove();
    }
    if (structuredData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-jsonld', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    return () => {
      const script = document.querySelector('script[data-jsonld="true"]');
      if (script) script.remove();
    };
  }, [fullTitle, description, keywords, canonicalUrl, ogType, ogImage, structuredData, baseUrl]);

  return null;
}